<script setup>
import { useShareLink } from "@/composables/useShareLink";
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { useModalogStore } from "@/stores/modalog.js";
import { useModalogEscape } from "@/composables/useModalogEscape.js";
import {
    DEFAULT_PROFILE_THEME,
    profileThemes,
} from "@/Components/Profile/profileThemes.js";
import {
    DEFAULT_WELCOME_EMOJI,
    firstEmoji,
    welcomeEmoji,
} from "@/Components/Profile/welcomeEmoji.js";
import IconUploadPhoto from "~/svg/icon-upload-photo.svg";
import CoverFocusPicker from "@/Components/Profile/CoverFocusPicker.vue";
import AvatarCropper from "@/Components/Profile/AvatarCropper.vue";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";
import { useToastStore } from "@/stores/toast.js";

const props = defineProps({
    // Field to focus when the modalog opens, e.g. "short_description" from
    // the profile hero's welcome-message pencil (BH-40).
    focus: {
        type: String,
        default: null,
    },
});

const modalogStore = useModalogStore();
const toastStore = useToastStore();

const welcomeMessageInput = ref(null);

// The stable-root instance survives closes, so focus on every open (and
// whenever a fresh `focus` prop arrives) rather than only on mount.
watch(
    () => [modalogStore.hasModalog, props.focus],
    async ([isOpen, focus]) => {
        if (!isOpen || focus !== "short_description") {
            return;
        }

        await nextTick();
        welcomeMessageInput.value?.scrollIntoView?.({ block: "center" });
        welcomeMessageInput.value?.focus();
    },
    { immediate: true },
);

useModalogEscape();
const user = useAuthUser();

const form = reactive({
    first_name: user.value?.first_name ?? "",
    last_name: user.value?.last_name ?? "",
    birthdate_month: user.value?.birthdate_month ?? "",
    birthdate_day: user.value?.birthdate_day ?? "",
    birthdate_year: user.value?.birthdate_year ?? "",
    username: user.value?.username ?? "",
    profile_theme: user.value?.profile_theme ?? DEFAULT_PROFILE_THEME,
    short_description: user.value?.short_description ?? "",
    welcome_emoji: user.value?.welcome_emoji ?? DEFAULT_WELCOME_EMOJI,
});

// Mirrors the users.short_description column length.
const WELCOME_MESSAGE_MAX = 80;

// --- Any-emoji entry beside the quick picks (BH-41) ---
// The "Other" box holds whatever isn't a quick pick; the OS emoji keyboard
// or a paste lands here and only the first emoji is kept.
const customEmoji = ref(
    welcomeEmoji.includes(form.welcome_emoji) ? "" : form.welcome_emoji,
);
const customEmojiError = ref(null);

const isCustomEmojiSelected = computed(
    () =>
        Boolean(customEmoji.value) && form.welcome_emoji === customEmoji.value,
);

function pickQuickEmoji(emoji) {
    form.welcome_emoji = emoji;
    customEmoji.value = "";
    customEmojiError.value = null;
}

function onCustomEmojiInput(event) {
    const value = event.target.value;

    if (value === "") {
        customEmoji.value = "";
        customEmojiError.value = null;
        return;
    }

    const emoji = firstEmoji(value);

    if (!emoji) {
        customEmoji.value = "";
        customEmojiError.value = "Please enter a single emoji.";
        return;
    }

    customEmoji.value = emoji;
    customEmojiError.value = null;
    form.welcome_emoji = emoji;
}

const errors = ref({});
const saving = ref(false);

const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const days = Array.from({ length: 31 }, (_, i) => i + 1);

// Youngest allowed account age is 13 (mirrors the backend birthdate rule).
const years = (() => {
    const latest = new Date().getFullYear() - 13;
    return Array.from({ length: latest - 1900 + 1 }, (_, i) => latest - i);
})();

const themeOptions = Object.entries(profileThemes).map(([slug, theme]) => ({
    slug,
    ...theme,
}));

// Themes carry per-theme gradient angles (GH-849); the wide-short swatch
// previews all render at a single flattened angle instead.
function swatchGradient(gradient) {
    return gradient.replace(
        /linear-gradient\(\s*[\d.]+deg/,
        "linear-gradient(132deg",
    );
}

// --- Avatar upload (direct to Cloudinary, confirmed on save) ---
const avatarPreview = ref(user.value?.avatar?.profile_photo ?? null);
const pendingPhotoId = ref(null);

// --- Avatar crop (GH-1217) ---
// The cropper needs the whole photo: the pending upload's secure_url, or
// a width-limited copy of the saved one. Null crop = automatic framing.
const avatarCropSrc = ref(
    user.value?.profile_photo
        ? `https://res.cloudinary.com/gift-hero/image/upload/c_limit,w_800,q_auto:good/${user.value.profile_photo}.jpg`
        : null,
);

function savedAvatarCrop() {
    const crop = user.value?.profile_photo_crop;

    return crop && Number.isFinite(crop.w) ? { ...crop } : null;
}

const avatarCrop = ref(savedAvatarCrop());
const showAvatarCropper = ref(false);
let avatarCropObjectUrl = null;

onBeforeUnmount(() => {
    if (avatarCropObjectUrl) {
        URL.revokeObjectURL(avatarCropObjectUrl);
    }
});

const avatarCropChanged = computed(() => {
    const saved = savedAvatarCrop();

    if (!saved || !avatarCrop.value) {
        return Boolean(saved) !== Boolean(avatarCrop.value);
    }

    return ["x", "y", "w", "h"].some(
        (key) => saved[key] !== avatarCrop.value[key],
    );
});

function resetAvatarCrop() {
    avatarCrop.value = null;
}
const uploading = ref(false);
const uploadError = ref(null);
const dragOver = ref(false);
const fileInput = ref(null);

function validateImageSize(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            resolve(image.width >= 500 && image.height >= 500);
            URL.revokeObjectURL(url);
        };
        image.onerror = () => {
            resolve(false);
            URL.revokeObjectURL(url);
        };
        image.src = url;
    });
}

async function handleFiles(fileList) {
    const file = fileList?.[0];
    uploadError.value = null;

    if (!file || !file.type.startsWith("image/")) {
        uploadError.value = "Please choose an image file.";
        return;
    }

    if (!(await validateImageSize(file))) {
        uploadError.value = "Images should be larger than 500x500.";
        return;
    }

    uploading.value = true;

    try {
        const data = await uploadToCloudinary(file, "user_profile_photo");

        pendingPhotoId.value = data.public_id;
        avatarPreview.value = data.secure_url;
        // The cropper works from the local file: same pixels as the upload,
        // no second download.
        if (avatarCropObjectUrl) {
            URL.revokeObjectURL(avatarCropObjectUrl);
        }
        avatarCropObjectUrl = URL.createObjectURL(file);
        avatarCropSrc.value = avatarCropObjectUrl;
        avatarCrop.value = null;
        showAvatarCropper.value = true;
    } catch {
        uploadError.value = "Upload failed — please try again.";
    } finally {
        uploading.value = false;
    }
}

async function uploadToCloudinary(file, photoType) {
    const { data: config } = await axios.get(
        route("uploads.signature", { photo_type: photoType }),
    );

    const formData = new FormData();
    formData.append("file", file);
    formData.append("api_key", config.api_key);
    formData.append("signature", config.signature);
    Object.entries(config.options).forEach(([key, value]) =>
        formData.append(key, value),
    );

    const response = await fetch(
        "https://api.cloudinary.com/v1_1/gift-hero/image/upload",
        { method: "POST", body: formData },
    );
    const data = await response.json();

    if (!data.public_id) {
        throw new Error(data.error?.message ?? "Upload failed");
    }

    return data;
}

function onDrop(event) {
    dragOver.value = false;
    handleFiles(event.dataTransfer?.files);
}

// --- Header photo upload (GH-893): same deferred Cloudinary flow ---
const HEADER_MIN_WIDTH = 800;

const headerPreview = ref(
    user.value?.header_photo
        ? `https://res.cloudinary.com/gift-hero/image/upload/c_lfill,h_170,w_680,g_center,q_auto:good/${user.value.header_photo}.jpg`
        : null,
);
const pendingHeaderPhotoId = ref(null);
const headerRemoved = ref(false);

// --- Header focal point (GH-1211): same picker as the list covers ---
// The picker needs the whole image, not the cropped preview above: the
// pending upload's secure_url, or a width-limited copy of the saved one.
const headerPickerSrc = ref(
    user.value?.header_photo
        ? `https://res.cloudinary.com/gift-hero/image/upload/c_limit,w_680,q_auto:good/${user.value.header_photo}.jpg`
        : null,
);

function savedHeaderFocus() {
    return Number.isFinite(user.value?.header_focus_x) &&
        Number.isFinite(user.value?.header_focus_y)
        ? { x: user.value.header_focus_x, y: user.value.header_focus_y }
        : null;
}

const headerFocus = ref(savedHeaderFocus());
// Collapsed for the saved photo, opened after a fresh upload — same as the
// avatar cropper.
const showHeaderFocus = ref(false);
const headerPickerValue = computed(() => headerFocus.value ?? { x: 50, y: 50 });

const headerFocusChanged = computed(() => {
    const saved = savedHeaderFocus();

    if (!saved || !headerFocus.value) {
        return Boolean(saved) !== Boolean(headerFocus.value);
    }

    return saved.x !== headerFocus.value.x || saved.y !== headerFocus.value.y;
});

function resetHeaderFocus() {
    headerFocus.value = null;
}
const headerUploading = ref(false);
const headerUploadError = ref(null);
const headerDragOver = ref(false);
const headerFileInput = ref(null);

// Header images are wide and short, so only a minimum width applies — the
// avatar's square 500x500 rule would reject legitimate banners.
function validateImageWidth(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            resolve(image.width >= HEADER_MIN_WIDTH);
            URL.revokeObjectURL(url);
        };
        image.onerror = () => {
            resolve(false);
            URL.revokeObjectURL(url);
        };
        image.src = url;
    });
}

async function handleHeaderFiles(fileList) {
    const file = fileList?.[0];
    headerUploadError.value = null;

    if (!file || !file.type.startsWith("image/")) {
        headerUploadError.value = "Please choose an image file.";
        return;
    }

    if (!(await validateImageWidth(file))) {
        headerUploadError.value = `Header images should be at least ${HEADER_MIN_WIDTH}px wide.`;
        return;
    }

    headerUploading.value = true;

    try {
        const data = await uploadToCloudinary(file, "user_header_photo");

        pendingHeaderPhotoId.value = data.public_id;
        headerRemoved.value = false;
        headerPreview.value = data.secure_url;
        headerPickerSrc.value = data.secure_url;
        // A fresh photo starts from the middle, not the old photo's point.
        headerFocus.value = { x: 50, y: 50 };
        showHeaderFocus.value = true;
    } catch {
        headerUploadError.value = "Upload failed — please try again.";
    } finally {
        headerUploading.value = false;
    }
}

function onHeaderDrop(event) {
    headerDragOver.value = false;
    handleHeaderFiles(event.dataTransfer?.files);
}

function removeHeaderPhoto() {
    pendingHeaderPhotoId.value = null;
    headerPreview.value = null;
    headerPickerSrc.value = null;
    headerFocus.value = null;
    headerUploadError.value = null;
    // Only flag a backend delete when a saved header photo exists.
    headerRemoved.value = Boolean(user.value?.header_photo);
}

// --- Save ---
const errorFor = (field) => errors.value[field]?.[0];

async function save() {
    if (saving.value) {
        return;
    }

    saving.value = true;
    errors.value = {};

    try {
        if (pendingPhotoId.value) {
            await axios.post(route("profile.photo.store"), {
                public_id: pendingPhotoId.value,
                crop: avatarCrop.value,
            });
        } else if (user.value?.profile_photo && avatarCropChanged.value) {
            // Re-cropping the current photo; the server skips the Cloudinary
            // delete when the public id is unchanged.
            await axios.post(route("profile.photo.store"), {
                public_id: user.value.profile_photo,
                crop: avatarCrop.value,
            });
        }

        if (pendingHeaderPhotoId.value) {
            await axios.post(route("profile.header_photo.store"), {
                public_id: pendingHeaderPhotoId.value,
                focus_x: headerFocus.value?.x ?? null,
                focus_y: headerFocus.value?.y ?? null,
            });
        } else if (headerRemoved.value) {
            await axios.delete(route("profile.header_photo.destroy"));
        } else if (user.value?.header_photo && headerFocusChanged.value) {
            // Moving the point on the current photo; the server skips the
            // Cloudinary delete when the public id is unchanged.
            await axios.post(route("profile.header_photo.store"), {
                public_id: user.value.header_photo,
                focus_x: headerFocus.value?.x ?? null,
                focus_y: headerFocus.value?.y ?? null,
            });
        }

        const { data } = await axios.put(route("profile.update"), { ...form });

        // The stable-root instance survives this save; stale pending refs
        // would re-post the same public id next time, which the server
        // treats as a photo swap (GH-1009).
        pendingPhotoId.value = null;
        pendingHeaderPhotoId.value = null;
        headerRemoved.value = false;

        modalogStore.closeModalog();
        toastStore.success("Your profile was updated.");

        // A username change moves the profile URL; land on the new one.
        if (data.user?.username && data.user.username !== user.value.username) {
            router.visit(
                route("user.giftlists", { username: data.user.username }),
            );
        } else {
            router.reload();
        }
    } catch (error) {
        if (error.response?.status === 422) {
            errors.value = error.response.data.errors ?? {};
        } else {
            errors.value = {
                general: ["Something went wrong — please try again."],
            };
        }
    } finally {
        saving.value = false;
    }
}

// --- Copy profile URL ---
const { shareLink } = useShareLink();
const copied = ref(false);

async function copyProfileUrl() {
    // Tagged so a signup through the shared profile is attributed (GH-1192).
    const url = shareLink(user.value?.profile_url, {
        type: "user",
        uuid: user.value?.uuid,
    });

    await navigator.clipboard?.writeText(url);
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
}

const inputClasses =
    "h-11 w-full rounded-[5px] border border-solid border-[#d0cac8] bg-white px-4 font-brand text-[15px] text-brand-navy focus:border-brand-navy focus:outline-none";

// bg-none drops the forms plugin's own chevron (appearance-none only hides
// the native control), otherwise it shows through behind the red caret.
const selectClasses = computed(
    () => `${inputClasses} appearance-none bg-none pr-9`,
);
</script>

<template>
    <!-- The root stays mounted and the overlay toggles on an inner v-if:
         closeModalog() clears hasModalog but leaves modalogType set, and a
         conditional root would engage the layout <transition> machinery,
         which strands the overlay mid-leave against the legacy fade CSS. -->
    <div>
        <div
            v-if="modalogStore.hasModalog"
            class="brand fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-navy/60 p-4 font-brand text-brand-navy sm:p-8"
            @click.self="modalogStore.closeModalog()"
        >
            <div
                class="relative my-auto w-full max-w-[815px] overflow-hidden rounded-[5px] bg-white shadow-brand-hero"
            >
                <!-- Gradient header (Hero Default wash per the mock) -->
                <header
                    class="flex items-center justify-between px-9 py-7"
                    :style="{
                        backgroundImage:
                            'linear-gradient(99deg, #ff9d3c -10%, #f21439 85%)',
                    }"
                >
                    <h2 class="text-[24px] font-bold text-white">
                        Manage Profile &amp; Theme
                    </h2>
                    <button
                        type="button"
                        class="grid size-8 place-items-center text-white transition hover:opacity-75"
                        aria-label="Close"
                        @click="modalogStore.closeModalog()"
                    >
                        <svg
                            viewBox="0 0 19 19"
                            class="size-[19px]"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            aria-hidden="true"
                        >
                            <path d="M2 2l15 15M17 2L2 17" />
                        </svg>
                    </button>
                </header>

                <div class="grid gap-9 p-9 md:grid-cols-2">
                    <!-- My Information -->
                    <section>
                        <h3 class="text-[20px] font-bold">My Information</h3>

                        <div class="mt-6 flex flex-col gap-5">
                            <label class="block">
                                <span
                                    class="mb-1.5 block text-[15px] font-bold text-[#3c3938]"
                                    >First Name</span
                                >
                                <input
                                    v-model="form.first_name"
                                    type="text"
                                    name="first_name"
                                    :class="inputClasses"
                                />
                                <span
                                    v-if="errorFor('first_name')"
                                    class="mt-1 block text-[13px] text-brand-red"
                                    >{{ errorFor("first_name") }}</span
                                >
                            </label>

                            <label class="block">
                                <span
                                    class="mb-1.5 block text-[15px] font-bold text-[#3c3938]"
                                    >Last Name</span
                                >
                                <input
                                    v-model="form.last_name"
                                    type="text"
                                    name="last_name"
                                    :class="inputClasses"
                                />
                                <span
                                    v-if="errorFor('last_name')"
                                    class="mt-1 block text-[13px] text-brand-red"
                                    >{{ errorFor("last_name") }}</span
                                >
                            </label>

                            <div>
                                <span
                                    class="mb-1.5 block text-[15px] font-bold text-[#3c3938]"
                                    >Birthday</span
                                >
                                <div class="grid grid-cols-3 gap-3">
                                    <span class="relative">
                                        <select
                                            v-model="form.birthdate_month"
                                            :class="selectClasses"
                                            aria-label="Birthday month"
                                        >
                                            <option value="">Month</option>
                                            <option
                                                v-for="(month, index) in months"
                                                :key="month"
                                                :value="index + 1"
                                            >
                                                {{ month }}
                                            </option>
                                        </select>
                                        <svg
                                            viewBox="0 0 14 12"
                                            class="pointer-events-none absolute right-3.5 top-1/2 h-2.5 w-3 -translate-y-1/2 text-brand-red"
                                            fill="currentColor"
                                            stroke="currentColor"
                                            stroke-width="2.5"
                                            stroke-linejoin="round"
                                            aria-hidden="true"
                                        >
                                            <path d="M3 3 L11 3 L7 9.5 Z" />
                                        </svg>
                                    </span>
                                    <span class="relative">
                                        <select
                                            v-model="form.birthdate_day"
                                            :class="selectClasses"
                                            aria-label="Birthday day"
                                        >
                                            <option value="">Day</option>
                                            <option
                                                v-for="day in days"
                                                :key="day"
                                                :value="day"
                                            >
                                                {{ day }}
                                            </option>
                                        </select>
                                        <svg
                                            viewBox="0 0 14 12"
                                            class="pointer-events-none absolute right-3.5 top-1/2 h-2.5 w-3 -translate-y-1/2 text-brand-red"
                                            fill="currentColor"
                                            stroke="currentColor"
                                            stroke-width="2.5"
                                            stroke-linejoin="round"
                                            aria-hidden="true"
                                        >
                                            <path d="M3 3 L11 3 L7 9.5 Z" />
                                        </svg>
                                    </span>
                                    <span class="relative">
                                        <select
                                            v-model="form.birthdate_year"
                                            :class="selectClasses"
                                            aria-label="Birthday year"
                                        >
                                            <option value="">Year</option>
                                            <option
                                                v-for="year in years"
                                                :key="year"
                                                :value="year"
                                            >
                                                {{ year }}
                                            </option>
                                        </select>
                                        <svg
                                            viewBox="0 0 14 12"
                                            class="pointer-events-none absolute right-3.5 top-1/2 h-2.5 w-3 -translate-y-1/2 text-brand-red"
                                            fill="currentColor"
                                            stroke="currentColor"
                                            stroke-width="2.5"
                                            stroke-linejoin="round"
                                            aria-hidden="true"
                                        >
                                            <path d="M3 3 L11 3 L7 9.5 Z" />
                                        </svg>
                                    </span>
                                </div>
                                <span
                                    v-if="
                                        errorFor('birthdate') ||
                                        errorFor('birthdate_month') ||
                                        errorFor('birthdate_day') ||
                                        errorFor('birthdate_year')
                                    "
                                    class="mt-1 block text-[13px] text-brand-red"
                                    >{{
                                        errorFor("birthdate") ||
                                        errorFor("birthdate_month") ||
                                        errorFor("birthdate_day") ||
                                        errorFor("birthdate_year")
                                    }}</span
                                >
                            </div>

                            <div>
                                <span
                                    class="mb-1.5 block text-[15px] font-bold text-[#3c3938]"
                                    >Username:</span
                                >
                                <div class="flex">
                                    <span
                                        class="flex h-11 items-center rounded-l-[5px] border border-r-0 border-solid border-[#d0cac8] bg-[#ececec] px-4 text-[14px] text-[#565a77]"
                                        >www.gifthero.com/</span
                                    >
                                    <input
                                        v-model="form.username"
                                        type="text"
                                        name="username"
                                        class="h-11 w-full min-w-0 rounded-r-[5px] border border-solid border-[#d0cac8] bg-white px-4 font-brand text-[15px] text-brand-navy focus:border-brand-navy focus:outline-none"
                                    />
                                </div>
                                <span
                                    v-if="errorFor('username')"
                                    class="mt-1 block text-[13px] text-brand-red"
                                    >{{ errorFor("username") }}</span
                                >

                                <p
                                    class="mt-3 text-[13px] leading-[1.5] text-[#3c3938]"
                                >
                                    Your profile web address is:<br />
                                    <span
                                        class="inline-flex items-center gap-2"
                                    >
                                        {{ user?.profile_url }}
                                        <button
                                            type="button"
                                            class="text-[#565a77] transition hover:text-brand-navy"
                                            :aria-label="
                                                copied
                                                    ? 'Copied'
                                                    : 'Copy profile URL'
                                            "
                                            @click="copyProfileUrl"
                                        >
                                            <svg
                                                viewBox="0 0 16 18"
                                                class="h-4 w-3.5"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M13.5 0H5.4C4.41 0 3.6 0.81 3.6 1.8V12.6C3.6 13.59 4.41 14.4 5.4 14.4H13.5C14.49 14.4 15.3 13.59 15.3 12.6V1.8C15.3 0.81 14.49 0 13.5 0ZM13.5 12.6H5.4V1.8H13.5V12.6ZM0 11.7V9.9H1.8V11.7H0ZM0 6.75H1.8V8.55H0V6.75ZM6.3 16.2H8.1V18H6.3V16.2ZM0 14.85V13.05H1.8V14.85H0ZM1.8 18C0.81 18 0 17.19 0 16.2H1.8V18ZM4.95 18H3.15V16.2H4.95V18ZM9.45 18V16.2H11.25C11.25 17.19 10.44 18 9.45 18ZM1.8 3.6V5.4H0C0 4.41 0.81 3.6 1.8 3.6Z"
                                                />
                                            </svg>
                                        </button>
                                        <span
                                            v-if="copied"
                                            class="text-[12px] font-semibold text-brand-navy"
                                            >Copied!</span
                                        >
                                    </span>
                                </p>
                            </div>

                            <div
                                class="flex items-start gap-3 rounded-[5px] border border-solid border-[#f5a623] bg-[#fff8ee] px-4 py-3.5"
                            >
                                <svg
                                    viewBox="0 0 22 21"
                                    class="mt-0.5 h-5 w-[22px] shrink-0"
                                    fill="#e8650d"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M11 0C4.92 0 0 4.7 0 10.5S4.92 21 11 21s11-4.7 11-10.5S17.08 0 11 0zm1.1 15.75H9.9v-2.1h2.2v2.1zm0-4.2H9.9V5.25h2.2v6.3z"
                                    />
                                </svg>
                                <p
                                    class="text-[14px] font-semibold leading-[1.4] text-[#e8650d]"
                                >
                                    Please Note: Other users can search for you
                                    by username or your full name.
                                </p>
                            </div>
                        </div>
                    </section>

                    <!-- My Theme & Avatar -->
                    <section>
                        <h3 class="text-[20px] font-bold">
                            My Theme &amp; Avatar
                        </h3>

                        <!-- The dashed drop zone extends behind the avatar, which
                         overlaps its left edge (per Figma). -->
                        <div class="relative mt-6 min-h-[88px]">
                            <button
                                type="button"
                                class="ml-[44px] block w-[calc(100%-44px)] rounded-[5px] border border-dashed py-8 pl-[64px] pr-5 text-left transition"
                                :class="
                                    dragOver
                                        ? 'border-brand-navy bg-brand-cream/60'
                                        : 'border-[#d0cac8]'
                                "
                                @click="fileInput.click()"
                                @dragover.prevent="dragOver = true"
                                @dragleave="dragOver = false"
                                @drop.prevent="onDrop"
                            >
                                <span
                                    class="block text-[14px] leading-[1.5] text-[#3c3938]"
                                >
                                    <template v-if="uploading"
                                        >Uploading…</template
                                    >
                                    <template v-else>
                                        <strong>Drag &amp; Drop</strong> an
                                        image here or
                                        <span
                                            class="font-bold underline underline-offset-2"
                                            >Upload a New Photo</span
                                        >
                                    </template>
                                </span>
                                <span
                                    class="mt-1 block text-[12px] text-[#8b8683]"
                                    >(images should be larger than
                                    500x500)</span
                                >
                            </button>

                            <div
                                class="pointer-events-none absolute left-0 top-1/2 grid size-[88px] -translate-y-1/2 place-items-center overflow-hidden rounded-full bg-brand-cream"
                            >
                                <img
                                    v-if="avatarPreview"
                                    :src="avatarPreview"
                                    alt="Profile photo"
                                    class="size-full object-cover"
                                />
                                <svg
                                    v-else
                                    viewBox="0 0 20 17"
                                    class="h-7 w-8 text-brand-tan"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <circle cx="10" cy="4" r="4" />
                                    <path
                                        d="M2 16.5c0-4 3.6-6 8-6s8 2 8 6H2z"
                                    />
                                </svg>
                            </div>
                            <input
                                ref="fileInput"
                                type="file"
                                accept="image/*"
                                class="sr-only"
                                @change="handleFiles($event.target.files)"
                            />
                        </div>
                        <p
                            v-if="uploadError"
                            class="mt-2 text-[13px] text-brand-red"
                        >
                            {{ uploadError }}
                        </p>

                        <!-- Crop (GH-1217): opens after an upload, or on demand
                             for the saved photo. Null keeps automatic framing. -->
                        <div
                            v-if="avatarCropSrc && !uploading"
                            class="mt-3"
                            data-test="avatar-crop-section"
                        >
                            <div
                                class="flex items-center justify-between gap-4"
                            >
                                <button
                                    type="button"
                                    class="text-[13px] font-semibold text-[#565a77] underline underline-offset-2 transition hover:text-brand-red"
                                    data-test="avatar-crop-toggle"
                                    @click="
                                        showAvatarCropper = !showAvatarCropper
                                    "
                                >
                                    {{
                                        showAvatarCropper
                                            ? "Hide crop"
                                            : "Adjust crop"
                                    }}
                                </button>
                                <button
                                    v-if="avatarCrop"
                                    type="button"
                                    class="text-[13px] font-semibold text-[#565a77] underline underline-offset-2 transition hover:text-brand-red"
                                    data-test="avatar-crop-reset"
                                    @click="resetAvatarCrop"
                                >
                                    Reset to automatic
                                </button>
                            </div>
                            <div v-if="showAvatarCropper" class="mt-3">
                                <p
                                    class="mb-3 text-[14px] font-semibold text-[#3c3938]"
                                >
                                    Drag to position and zoom to frame your
                                    photo.
                                </p>
                                <AvatarCropper
                                    :model-value="avatarCrop"
                                    :src="avatarCropSrc"
                                    alt="Profile photo"
                                    @update:model-value="avatarCrop = $event"
                                />
                            </div>
                        </div>

                        <!-- Welcome message + emoji (BH-40/41): lives here, next
                             to the photo, so the hero chip can be edited without
                             leaving the lists. -->
                        <div class="mt-7">
                            <label
                                class="mb-1.5 block text-[15px] font-bold text-[#3c3938]"
                                for="profile-welcome-message"
                                >Welcome Message</label
                            >
                            <input
                                id="profile-welcome-message"
                                ref="welcomeMessageInput"
                                v-model="form.short_description"
                                type="text"
                                name="short_description"
                                :maxlength="WELCOME_MESSAGE_MAX"
                                placeholder="Say hi to people visiting your lists"
                                :class="inputClasses"
                            />
                            <span
                                class="mt-2 block text-[13px] leading-snug text-[#3c3938]/80"
                            >
                                Shown in your profile header.
                                {{
                                    WELCOME_MESSAGE_MAX -
                                    form.short_description.length
                                }}
                                characters left.
                            </span>
                            <span
                                v-if="errorFor('short_description')"
                                class="mt-1 block text-[13px] text-brand-red"
                                >{{ errorFor("short_description") }}</span
                            >

                            <div
                                class="mt-3 flex flex-wrap gap-2"
                                role="radiogroup"
                                aria-label="Welcome message emoji"
                            >
                                <button
                                    v-for="emoji in welcomeEmoji"
                                    :key="emoji"
                                    type="button"
                                    role="radio"
                                    :aria-checked="form.welcome_emoji === emoji"
                                    class="grid size-10 place-items-center rounded-[5px] bg-white text-[22px] leading-none transition"
                                    :class="
                                        form.welcome_emoji === emoji
                                            ? 'border-[3px] border-solid border-[#08b363]'
                                            : 'border border-solid border-[#d0cac8] hover:border-brand-navy'
                                    "
                                    @click="pickQuickEmoji(emoji)"
                                >
                                    {{ emoji }}
                                </button>

                                <!-- Any other emoji: the OS keyboard or a paste -->
                                <label class="inline-flex items-center gap-2">
                                    <input
                                        :value="customEmoji"
                                        type="text"
                                        name="welcome_emoji_custom"
                                        inputmode="text"
                                        autocomplete="off"
                                        aria-label="Other emoji"
                                        placeholder="＋"
                                        class="size-10 rounded-[5px] bg-white p-0 text-center text-[22px] leading-none transition focus:outline-none"
                                        :class="
                                            isCustomEmojiSelected
                                                ? 'border-[3px] border-solid border-[#08b363]'
                                                : 'border border-solid border-[#d0cac8] hover:border-brand-navy focus:border-brand-navy'
                                        "
                                        @input="onCustomEmojiInput"
                                    />
                                    <span class="text-[13px] text-[#3c3938]/80"
                                        >or type any emoji</span
                                    >
                                </label>
                            </div>
                            <span
                                v-if="
                                    customEmojiError ||
                                    errorFor('welcome_emoji')
                                "
                                class="mt-1 block text-[13px] text-brand-red"
                                >{{
                                    customEmojiError ||
                                    errorFor("welcome_emoji")
                                }}</span
                            >
                        </div>

                        <div class="mt-7">
                            <span
                                class="mb-2.5 block text-[15px] font-bold text-[#3c3938]"
                                >Select a Theme</span
                            >
                            <div class="grid w-fit grid-cols-3 gap-3">
                                <button
                                    v-for="option in themeOptions"
                                    :key="option.slug"
                                    type="button"
                                    class="rounded-[5px] bg-white text-left transition"
                                    :class="
                                        form.profile_theme === option.slug
                                            ? 'border-[3px] border-solid border-[#08b363] p-1'
                                            : 'border border-solid border-[#d0cac8] p-1.5 hover:border-brand-navy'
                                    "
                                    :aria-pressed="
                                        form.profile_theme === option.slug
                                    "
                                    @click="form.profile_theme = option.slug"
                                >
                                    <span
                                        class="relative block h-[55px] w-[91px] overflow-hidden rounded-[2px]"
                                        :style="{
                                            backgroundImage: swatchGradient(
                                                option.gradient,
                                            ),
                                        }"
                                    >
                                        <!-- Mini corner wedge, mirroring the hero motif -->
                                        <span
                                            class="absolute bottom-0 left-0 h-full w-[38px]"
                                            :style="{
                                                background: option.wedge,
                                                clipPath:
                                                    'polygon(0 35%, 100% 100%, 0 100%)',
                                            }"
                                        ></span>
                                    </span>
                                    <span
                                        class="mt-1 block text-[12px] font-semibold text-[#1d2333]"
                                        >{{ option.label }}</span
                                    >
                                </button>
                            </div>
                        </div>

                        <!-- Upload Custom Photo Header (GH-893) -->
                        <div class="mt-7">
                            <span
                                class="block text-[15px] font-bold text-[#3c3938]"
                                >Upload Custom Photo Header:</span
                            >
                            <p
                                class="mt-1 text-[13px] leading-[1.5] text-[#3c3938]"
                            >
                                You may select a theme <em>and</em> add a header
                                image to your profile (or leave this blank to
                                only use the theme).
                            </p>

                            <button
                                type="button"
                                class="mt-3 block w-full overflow-hidden rounded-[5px] border border-dashed text-left transition"
                                :class="
                                    headerDragOver
                                        ? 'border-brand-navy bg-brand-cream/60'
                                        : 'border-[#d0cac8]'
                                "
                                @click="headerFileInput.click()"
                                @dragover.prevent="headerDragOver = true"
                                @dragleave="headerDragOver = false"
                                @drop.prevent="onHeaderDrop"
                            >
                                <img
                                    v-if="headerPreview && !headerUploading"
                                    :src="headerPreview"
                                    alt="Profile header photo"
                                    class="h-[85px] w-full object-cover"
                                />
                                <span
                                    v-else
                                    class="flex items-center gap-4 px-5 py-6"
                                >
                                    <IconUploadPhoto
                                        class="h-8 w-[34px] shrink-0"
                                        aria-hidden="true"
                                    />
                                    <span
                                        class="block text-[14px] leading-[1.5] text-[#3c3938]"
                                    >
                                        <template v-if="headerUploading"
                                            >Uploading…</template
                                        >
                                        <template v-else>
                                            <strong>Drag &amp; Drop</strong> an
                                            image here or
                                            <span
                                                class="font-bold underline underline-offset-2"
                                                >Upload a New Photo</span
                                            >
                                        </template>
                                    </span>
                                </span>
                            </button>
                            <input
                                ref="headerFileInput"
                                type="file"
                                accept="image/*"
                                class="sr-only"
                                @change="handleHeaderFiles($event.target.files)"
                            />

                            <div
                                class="mt-2 flex items-center justify-between gap-3"
                            >
                                <p
                                    v-if="headerUploadError"
                                    class="text-[13px] text-brand-red"
                                >
                                    {{ headerUploadError }}
                                </p>
                                <button
                                    v-if="headerPreview"
                                    type="button"
                                    class="ml-auto text-[13px] font-semibold text-[#565a77] underline underline-offset-2 transition hover:text-brand-red"
                                    @click="removeHeaderPhoto"
                                >
                                    Remove Header Photo
                                </button>
                            </div>

                            <!-- Focus point (GH-1211): pending upload or the
                                 saved header, previewed at the hero's aspect -->
                            <div
                                v-if="headerPickerSrc && !headerUploading"
                                class="mt-4"
                                data-test="header-focus-section"
                            >
                                <div
                                    class="flex items-center justify-between gap-4"
                                >
                                    <button
                                        type="button"
                                        class="text-[13px] font-semibold text-[#565a77] underline underline-offset-2 transition hover:text-brand-red"
                                        data-test="header-focus-toggle"
                                        @click="
                                            showHeaderFocus = !showHeaderFocus
                                        "
                                    >
                                        {{
                                            showHeaderFocus
                                                ? "Hide focus"
                                                : "Adjust focus"
                                        }}
                                    </button>
                                    <button
                                        v-if="headerFocus"
                                        type="button"
                                        class="shrink-0 text-[13px] font-semibold text-[#565a77] underline underline-offset-2 transition hover:text-brand-red"
                                        data-test="header-focus-reset"
                                        @click="resetHeaderFocus"
                                    >
                                        Reset to center
                                    </button>
                                </div>
                                <div v-if="showHeaderFocus" class="mt-3">
                                    <p
                                        class="mb-3 text-[14px] font-semibold text-[#3c3938]"
                                    >
                                        Tap the photo to choose what stays in
                                        view.
                                    </p>
                                    <CoverFocusPicker
                                        :model-value="headerPickerValue"
                                        :src="headerPickerSrc"
                                        alt="Profile header photo"
                                        preview-aspect="1472 / 264"
                                        @update:model-value="
                                            headerFocus = $event
                                        "
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="mt-9 flex items-center justify-end gap-4">
                            <span
                                v-if="errors.general"
                                class="text-[13px] text-brand-red"
                                >{{ errors.general[0] }}</span
                            >
                            <button
                                type="button"
                                class="inline-flex items-center gap-2 rounded-brand bg-brand-navy px-8 py-3.5 text-[17px] font-bold text-white transition hover:bg-brand-navy/90"
                                :class="saving ? '' : 'disabled:opacity-60'"
                                :disabled="
                                    saving || uploading || headerUploading
                                "
                                @click="save"
                            >
                                {{ saving ? "Saving" : "Save Updates" }}
                                <BrandThrobber v-if="saving" />
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    </div>
</template>
